import { useState, useEffect } from "react";
import {
  listSkills,
  type Skill,
  type AgentSkillBinding,
} from "../api/client.js";

export function SkillPickerModal({
  agentId,
  boundSkills,
  onBind,
  onClose,
}: {
  agentId: string;
  boundSkills: AgentSkillBinding[];
  onBind: (skillId: string) => Promise<void>;
  onClose: () => void;
}) {
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [binding, setBinding] = useState<string | null>(null);

  useEffect(() => {
    listSkills()
      .then((res) => setAllSkills(res.skills))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const boundIds = new Set(boundSkills.map((s) => s.id));
  const availableSkills = allSkills
    .filter((s) => s.approved && !boundIds.has(s.id))
    .filter((s) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.skillId.toLowerCase().includes(q) ||
        (s.author?.toLowerCase().includes(q) ?? false)
      );
    });

  async function handleBind(skillId: string) {
    setBinding(skillId);
    try {
      await onBind(skillId);
    } finally {
      setBinding(null);
    }
  }

  const riskColors: Record<string, string> = {
    low: "badge-green",
    medium: "badge-amber",
    high: "badge-red",
    critical: "badge-red",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-lg animate-slide-up rounded-2xl bg-white shadow-elevated">
        {/* Header */}
        <div className="border-b border-ink-100 px-6 pb-3 pt-5">
          <h2 className="text-base font-semibold text-ink-900">Add Skill</h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Select an approved skill to bind to this agent
          </p>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills..."
            className="input mt-3 text-sm"
            autoFocus
          />
        </div>

        {/* Skill list */}
        <div className="max-h-80 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
            </div>
          ) : availableSkills.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-xs text-ink-400">
                {allSkills.length === 0
                  ? "No skills available. Upload a SKILL.md first."
                  : search
                    ? "No matching skills found"
                    : "All approved skills are already bound"}
              </p>
            </div>
          ) : (
            availableSkills.map((skill) => (
              <div
                key={skill.id}
                className="flex items-center gap-3 rounded-lg px-4 py-3 hover:bg-surface-1"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-2">
                  <svg
                    className="h-4 w-4 text-ink-500"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M3 1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                    <path d="M5 5h6M5 8h4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink-800">{skill.name}</span>
                    <span className={riskColors[skill.riskLevel] ?? "badge-gray"}>
                      {skill.riskLevel}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-2xs text-ink-400">
                    <span className="font-mono">{skill.skillId}</span>
                    <span>v{skill.version}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleBind(skill.id)}
                  disabled={binding === skill.id}
                  className="btn-primary text-xs px-3 py-1.5"
                >
                  {binding === skill.id ? "..." : "Bind"}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-ink-100 px-6 py-3">
          <button onClick={onClose} className="btn-secondary text-xs w-full">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
